import { getBasicCourseVariantSource, getBasicCourseSkillLabels, getBasicCourseSkillNote } from "../../matura/basicCourseTaskInfo";
// src/components/TaskRunner.js
import React, { useState, useEffect, useContext, useMemo } from "react";
import { createPortal } from "react-dom";
import { BlockMath, InlineMath } from "react-katex";
import { useLocation } from "react-router-dom";
import "katex/dist/katex.min.css";

import EdgeDrawer from "../ui/EdgeDrawer";
import ExamCalculator from "../ExamCalculator";
import FormulaSheet from "../FormulaSheet";
import HorizontalScroll from "../ui/HorizontalScroll";
import useFabTop from "../ui/useFabTop";
import TaskInlineWhiteboard from "./TaskInlineWhiteboard";
import { TaskPreviewContext } from "../../context/TaskPreviewContext";
import { AuthContext } from "../AuthContext";
import MaturaOriginalSourceLinks from "../MaturaOriginalSourceLinks";
import {
  buildLearningDraftKey,
  readLearningDraft,
  writeLearningDraft,
} from "../../utils/localLearningDrafts";

const WZORY_URL = process.env.PUBLIC_URL + "/assets/wybrane_wzory_matematyczne_EM2023.pdf";
const CALCULATOR_DRAWER_WIDTH = 420;
const FORMULA_SHEET_DRAWER_WIDTH = 740;
const LEARNING_DRAFT_COMPONENTS = { BlockMath, InlineMath };
function Fab({ side = "left", top = 96, title, ariaLabel, icon, onClick }) {
  const [node, setNode] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    const el = document.createElement("div");
    document.body.appendChild(el);
    setNode(el);
    return () => document.body.removeChild(el);
  }, []);

  if (!node) return null;

  const base = {
    position: "fixed",
    [side]: 12,
    top,
    zIndex: 998,
    width: 56,
    height: 56,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    borderRadius: "9999px",
    background: "#e58b00",
    color: "#171717",
    border: "1px solid rgba(23,23,23,.82)",
    boxShadow: "0 4px 12px rgba(0,0,0,.30)",
    transition: "top .12s ease-out, transform .12s ease, box-shadow .18s ease",
  };

  const hover = hovered
    ? {
        background: "#f5a31a",
        transform: "translateY(-1px) scale(1.03)",
        boxShadow: "0 6px 16px rgba(0,0,0,.34)",
      }
    : {};

  const active = pressed ? { transform: "translateY(0) scale(0.97)" } : {};

  return createPortal(
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      aria-label={ariaLabel}
      title={title}
      style={{ ...base, ...hover, ...active }}
    >
      {icon}
    </button>,
    node
  );
}

function formatTaskLabel(task, index) {
  const base = typeof task?.displayBaseNumber === "number" ? task.displayBaseNumber : index + 1;
  if (task && task.maturaGroupId && typeof task.maturaGroupPart === "number") {
    return `${base}.${task.maturaGroupPart}`;
  }
  return `${base}`;
}

function generationOptionsForViewer(options) {
  return options;
}

const SOURCE_MONTHS = {
  STY: "styczeń",
  LUT: "luty",
  MAR: "marzec",
  KWI: "kwiecień",
  MAJ: "maj",
  CZE: "czerwiec",
  LIP: "lipiec",
  SIE: "sierpień",
  WRZ: "wrzesień",
  PAZ: "październik",
  LIS: "listopad",
  GRU: "grudzień",
};

function formatTaskSource(task) {
  const source = task?.basicCourseSource ? getBasicCourseVariantSource(task) : task?.source;
  if (!source || !["original", "random"].includes(source.variant) || !source.session || !source.taskNumber) return "";

  const sessionParts = String(source.session || "").split("_");
  const year = sessionParts.find((part) => /^\d{4}$/.test(part));
  const monthCode = sessionParts.find((part) => SOURCE_MONTHS[part]);
  const dateLabel = [monthCode ? SOURCE_MONTHS[monthCode] : "", year || ""]
    .filter(Boolean)
    .join(" ");
  const examLabel = source.exam === "E8" ? "E8" : source.exam;
  const taskLabel =
    source.taskNumber != null && String(source.taskNumber).trim()
      ? `zad. ${source.taskNumber}`
      : "";

  const sourceText = [examLabel, "CKE", dateLabel, taskLabel]
    .filter(Boolean)
    .join(" • ");
  return source.variant === "random" ? `Wariant na podstawie: ${sourceText}` : sourceText;
}

export default function TaskRunner({
  generateTasks,
  renderTask,
  renderAnswer,
  renderExplanation,
  generationModes = [],
  defaultGenerationMode = "random",
  fixedGenerationMode = null,
  draftScope = "",
  hideUtilityFabs = false,
  showTaskPoints = false,
  getTaskPoints = null,
  getTaskGroupKey = null,
}) {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const isPublicDemoPreview = location.pathname.startsWith("/demo/matura")
    && new URLSearchParams(location.search).get("publicPreview") === "1";
  const isAdmin = auth?.user?.role === "admin" && !isPublicDemoPreview;
  const draftUserId = location.pathname.startsWith("/demo/matura")
    ? "guest"
    : auth?.user?.email || "guest";
  const hasOriginalMaturaModes = generationModes.some(
    (mode) => mode?.generationOptions?.variant === "original"
  );
  const visibleGenerationModes = useMemo(
    () => isAdmin
      ? generationModes
      : generationModes.filter((mode) => !(
          mode?.generationOptions?.variant === "original" &&
          mode?.generationOptions?.session === "2026_MAJ"
        )),
    [generationModes, isAdmin]
  );
  const effectiveGenerationModes = useMemo(
    () =>
      hasOriginalMaturaModes &&
      !visibleGenerationModes.some((mode) => mode.value === "all_types")
        ? [
            ...visibleGenerationModes,
            {
              value: "all_types",
              label: "Wszystkie zadania",
              generationOptions: { allTypes: true },
            },
          ]
        : visibleGenerationModes,
    [hasOriginalMaturaModes, visibleGenerationModes]
  );
  const generationModeCounts = useMemo(() => {
    if (!hasOriginalMaturaModes) return {};

    return Object.fromEntries(
      effectiveGenerationModes.map((mode) => {
        try {
          const generated = mode?.generationOptions
            ? generateTasks(generationOptionsForViewer(mode.generationOptions, isAdmin))
            : mode?.value
              ? generateTasks({ variant: mode.value })
              : generateTasks();
          return [mode.value, Array.isArray(generated) ? generated.length : 0];
        } catch {
          return [mode.value, null];
        }
      })
    );
  }, [effectiveGenerationModes, generateTasks, hasOriginalMaturaModes, isAdmin]);
  const previewCfg = useContext(TaskPreviewContext);
  const isPreviewMode = Boolean(previewCfg?.enabled);
  const previewFirstN = Number(previewCfg?.firstN || 3);
  const previewFixedTaskIndex = Number.isInteger(previewCfg?.fixedTaskIndex)
    ? Number(previewCfg.fixedTaskIndex)
    : null;
  const hideTaskHeader = isPreviewMode && previewCfg?.hideTaskHeader !== false;
  const previewLabel = String(previewCfg?.label || "Przykladowe zadanie");

  const pickPreviewTask = (allTasks) => {
    const tasksArray = Array.isArray(allTasks) ? allTasks : [];
    if (!isPreviewMode) return tasksArray;
    if (tasksArray.length === 0) return [];

    const cappedN = Math.max(1, Math.min(previewFirstN, tasksArray.length));
    const pool = tasksArray.slice(0, cappedN);
    const picked =
      previewFixedTaskIndex !== null
        ? pool[((previewFixedTaskIndex % pool.length) + pool.length) % pool.length]
        : pool[Math.floor(Math.random() * pool.length)];
    return picked ? [picked] : [tasksArray[0]];
  };

  const initialGenerationMode =
    fixedGenerationMode ||
    effectiveGenerationModes.find((mode) => mode.value === defaultGenerationMode)?.value ||
    effectiveGenerationModes[0]?.value ||
    null;
  const draftKey = useMemo(
    () =>
      buildLearningDraftKey({
        kind: "exercises",
        userId: draftUserId,
        pathname: location.pathname,
        scope: [fixedGenerationMode || "default", draftScope].filter(Boolean).join(":"),
      }),
    [draftScope, draftUserId, fixedGenerationMode, location.pathname]
  );
  const initialDraft = useMemo(
    () => isPreviewMode ? null : readLearningDraft(draftKey, LEARNING_DRAFT_COMPONENTS),
    [draftKey, isPreviewMode]
  );
  const restoredGenerationMode =
    initialDraft?.generationMode &&
    (initialDraft.generationMode === fixedGenerationMode ||
      effectiveGenerationModes.some((mode) => mode.value === initialDraft.generationMode))
      ? initialDraft.generationMode
      : initialGenerationMode;
  const [generationMode, setGenerationMode] = useState(restoredGenerationMode);
  const generateForMode = (mode) => {
    const modeConfig = effectiveGenerationModes.find((item) => item.value === mode);
    if (modeConfig?.generationOptions) {
      return generateTasks(generationOptionsForViewer(modeConfig.generationOptions, isAdmin));
    }
    return mode ? generateTasks({ variant: mode }) : generateTasks();
  };
  const [tasks, setTasks] = useState(() =>
    Array.isArray(initialDraft?.tasks) && initialDraft.tasks.length
      ? initialDraft.tasks
      : pickPreviewTask(generateForMode(restoredGenerationMode))
  );
  const [revealed, setRevealed] = useState(() =>
    Array.isArray(initialDraft?.revealed) && initialDraft.revealed.length === tasks.length
      ? initialDraft.revealed
      : Array(tasks.length).fill(false)
  );
  const [explained, setExplained] = useState(() =>
    Array.isArray(initialDraft?.explained) && initialDraft.explained.length === tasks.length
      ? initialDraft.explained
      : Array(tasks.length).fill(false)
  );
  const [taskWhiteboards, setTaskWhiteboards] = useState(() => (
    Array.isArray(initialDraft?.taskWhiteboards) && initialDraft.taskWhiteboards.length === tasks.length
      ? initialDraft.taskWhiteboards
      : tasks.map(() => null)
  ));

  const [calcOpen, setCalcOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const fabTop = useFabTop();
  const activeModeConfig = effectiveGenerationModes.find(
    (item) => item.value === generationMode
  );
  const isOriginalMode =
    fixedGenerationMode === "original" ||
    activeModeConfig?.generationOptions?.variant === "original" ||
    generationMode === "original";

  useEffect(() => {
    const onKey = (e) => {
      const k = e.key?.toLowerCase();
      if (e.ctrlKey && e.altKey && k === "k") {
        e.preventDefault();
        setCalcOpen((v) => !v);
      }
      if (e.ctrlKey && e.altKey && k === "w") {
        e.preventDefault();
        setSheetOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setCalcOpen(false);
        setSheetOpen(false);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (isPreviewMode || !Array.isArray(tasks) || !tasks.length) return;
    writeLearningDraft(draftKey, {
      generationMode,
      tasks,
      revealed,
      explained,
      taskWhiteboards,
    });
  }, [draftKey, explained, generationMode, isPreviewMode, revealed, taskWhiteboards, tasks]);

  const reset = () => {
    const next = pickPreviewTask(generateForMode(generationMode));
    setTasks(next);
    setRevealed(Array(next.length).fill(false));
    setExplained(Array(next.length).fill(false));
    setTaskWhiteboards(next.map(() => null));
  };

  const rerollTask = (i) => {
    const generated = generateForMode(generationMode);
    const rerolled = pickPreviewTask(generated);
    const nt = isPreviewMode ? rerolled[0] : generated[i];
    if (!nt) return;
    const groupKey = !isPreviewMode && getTaskGroupKey?.(tasks[i]);
    const indexes = groupKey ? tasks.map((task, index) => getTaskGroupKey(task) === groupKey ? index : -1).filter(index => index >= 0) : [i];
    const up = [...tasks];
    indexes.forEach(index => { up[index] = isPreviewMode ? nt : generated[index]; });
    setTasks(up);

    const r = [...revealed];
    indexes.forEach(index => { r[index] = false; });
    setRevealed(r);

    const e = [...explained];
    indexes.forEach(index => { e[index] = false; });
    setExplained(e);

    const boards = [...taskWhiteboards];
    indexes.forEach(index => { boards[index] = null; });
    setTaskWhiteboards(boards);
  };

  const changeGenerationMode = (nextMode) => {
    if (nextMode === generationMode) return;
    const next = pickPreviewTask(generateForMode(nextMode));
    setGenerationMode(nextMode);
    setTasks(next);
    setRevealed(Array(next.length).fill(false));
    setExplained(Array(next.length).fill(false));
    setTaskWhiteboards(next.map(() => null));
  };

  return (
    <div>
      {!hideUtilityFabs && !calcOpen && (
        <Fab
          side="left"
          top={fabTop}
          title="Kalkulator (Ctrl+Alt+K)"
          ariaLabel="Otworz kalkulator"
          onClick={() => setCalcOpen(true)}
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="2.5" width="18" height="19" rx="3" fill="currentColor" opacity="0.18" />
              <rect x="6" y="5.5" width="12" height="3.4" rx="1" fill="currentColor" />
              <circle cx="8.25" cy="11.5" r="1.2" fill="currentColor" />
              <circle cx="12" cy="11.5" r="1.2" fill="currentColor" />
              <circle cx="15.75" cy="11.5" r="1.2" fill="currentColor" />
              <circle cx="8.25" cy="15.25" r="1.2" fill="currentColor" />
              <circle cx="12" cy="15.25" r="1.2" fill="currentColor" />
              <circle cx="15.75" cy="15.25" r="1.2" fill="currentColor" />
            </svg>
          }
        />
      )}

      {!hideUtilityFabs && !sheetOpen && (
        <Fab
          side="right"
          top={fabTop}
          title="Karta wzorow (Ctrl+Alt+W)"
          ariaLabel="Otworz karte wzorow"
          onClick={() => setSheetOpen(true)}
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="4" y="3" width="14" height="18" rx="2" fill="currentColor" opacity="0.18" />
              <rect x="6.5" y="6" width="9" height="1.8" rx="0.9" fill="currentColor" />
              <rect x="6.5" y="9.2" width="9" height="1.8" rx="0.9" fill="currentColor" />
              <rect x="6.5" y="12.4" width="9" height="1.8" rx="0.9" fill="currentColor" />
              <rect x="6.5" y="15.6" width="6" height="1.8" rx="0.9" fill="currentColor" />
            </svg>
          }
        />
      )}

      {!fixedGenerationMode && effectiveGenerationModes.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
            margin: "4px 0 20px",
            padding: "12px 14px",
            border: "1px solid color-mix(in srgb, var(--color-text) 24%, transparent)",
            borderRadius: 14,
            background: "color-mix(in srgb, var(--color-text) 6%, transparent)",
          }}
        >
          <div>
            <strong>Tryb zadań</strong>
            <div style={{ marginTop: 2, fontSize: "0.82rem", opacity: 0.72 }}>
              {isOriginalMode
                ? "Dokładne dane i odpowiedzi z arkusza źródłowego."
                : "Nowe warianty tworzone na podstawie zadania źródłowego."}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {effectiveGenerationModes.map((mode) => {
              const active = generationMode === mode.value;
              const count = generationModeCounts[mode.value];
              return (
                <button
                  key={mode.value}
                  type="button"
                  className="btn-secondary"
                  aria-pressed={active}
                  onClick={() => changeGenerationMode(mode.value)}
                  style={{
                    padding: "5px 12px",
                    fontSize: "0.84rem",
                    fontWeight: active ? 700 : 500,
                    background: active
                      ? "color-mix(in srgb, var(--color-accent) 24%, transparent)"
                      : undefined,
                    borderColor: active ? "var(--color-accent)" : undefined,
                  }}
                >
                  {mode.label}
                  {Number.isInteger(count) ? ` (${count})` : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tasks.map((task, i) => {
        const showExtraMargin = task?.type === "point_location_preview";
        const sourceLabel = formatTaskSource(task);
        const skillLabels = getBasicCourseSkillLabels(task);
        const skillNote = getBasicCourseSkillNote(task);
        const taskPoints = showTaskPoints
          ? (typeof getTaskPoints === "function" ? getTaskPoints(task) : task?.data?.maxPoints ?? task?.maxPoints ?? 1)
          : null;

        return (
          <div key={i} style={{ marginBottom: "14px" }}>
            {!isPreviewMode && i === 0 && (
              <hr
                style={{
                  margin: "16px 0",
                  border: "none",
                  borderTop: "2px solid",
                  borderColor: "var(--color-text)",
                }}
              />
            )}

            {!hideTaskHeader && !isPreviewMode && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <h4 style={{ margin: 0 }}>
                    Zadanie {formatTaskLabel(task, i)}
                    {taskPoints != null ? ` (${taskPoints} pkt)` : ""}
                  </h4>
                  {sourceLabel && (
                    <span
                      style={{
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        opacity: 0.72,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {sourceLabel}
                    </span>
                  )}
                </div>
                {!isOriginalMode && (
                  <button className="btn-secondary" onClick={() => rerollTask(i)} style={{ fontSize: "0.8rem", padding: "4px 8px" }}>
                    Wylosuj inne
                  </button>
                )}
              </div>
            )}

            {isPreviewMode && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 700, opacity: 0.95 }}>{previewLabel}</div>
                  {sourceLabel && (
                    <div style={{ marginTop: 2, fontSize: "0.72rem", fontWeight: 700, opacity: 0.68 }}>
                      {sourceLabel}
                    </div>
                  )}
                </div>
                {!isOriginalMode && (
                  <button className="btn-secondary" onClick={() => rerollTask(i)} style={{ fontSize: "0.78rem", padding: "3px 8px" }}>
                    Wylosuj inne
                  </button>
                )}
              </div>
            )}

            {!isPreviewMode && skillLabels.length > 0 && (
              <p style={{fontSize:"0.8rem", fontWeight:400, margin:"8px 0", color:"var(--color-text)"}}>
                Umiejętności: {skillLabels.join(" · ")}
                {skillNote && <span style={{display:"block", marginTop:4}}>{skillNote}</span>}
              </p>
            )}
            {renderTask(task)}
            <MaturaOriginalSourceLinks task={task} />

            <div style={{ marginTop: revealed[i] ? (showExtraMargin ? "32px" : "6px") : 6 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {!revealed[i] ? (
                  <button
                    className="btn-secondary btn-task-action"
                    onClick={() => {
                      const c = [...revealed];
                      c[i] = true;
                      setRevealed(c);
                    }}
                  >
                    Pokaz odpowiedz
                  </button>
                ) : (
                  <button
                    className="btn-secondary btn-task-action"
                    onClick={() => {
                      const c = [...explained];
                      c[i] = !c[i];
                      setExplained(c);
                    }}
                  >
                    {explained[i] ? "Ukryj wyjasnienie" : "Pokaz wyjasnienie"}
                  </button>
                )}
                <TaskInlineWhiteboard
                  renderTaskPreview={() => renderTask(task)}
                  alignRight
                  initialBoardState={taskWhiteboards[i]}
                  onBoardStateChange={(boardState) => {
                    setTaskWhiteboards((previous) => {
                      const next = [...previous];
                      next[i] = boardState || null;
                      return next;
                    });
                  }}
                />
              </div>
            </div>

            {revealed[i] && (
              <div style={{ marginTop: "12px" }}>
                <p>
                  <strong>Odpowiedz:</strong> {renderAnswer(task)}
                </p>

                {explained[i] && (
                  <div style={{ marginTop: "8px" }}>
                    <HorizontalScroll>{renderExplanation(task)}</HorizontalScroll>
                  </div>
                )}
              </div>
            )}

            {!isPreviewMode && (
              <hr
                style={{
                  margin: "12px 0",
                  border: "none",
                  borderTop: "2px solid",
                  borderColor: "var(--color-text)",
                }}
              />
            )}
          </div>
        );
      })}

      {!isPreviewMode && (
        <div style={{ textAlign: "center", marginTop: "30px", display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
          <button className="btn-secondary" onClick={reset}>
            {isOriginalMode ? "Wczytaj oryginał" : "Wylosuj nowe zadania"}
          </button>
          <button className="btn-secondary" onClick={() => setRevealed(Array(tasks.length).fill(true))}>
            Sprawdz wszystkie odpowiedzi
          </button>
        </div>
      )}

      <EdgeDrawer
        side="left"
        isOpen={calcOpen}
        onOpenChange={setCalcOpen}
        width={CALCULATOR_DRAWER_WIDTH}
        title="Kalkulator maturalny"
        showHandle={false}
        offsetTop={0}
        background="linear-gradient(180deg, rgba(255,153,0,0.36) 0%, rgba(255,190,70,0.36) 100%)"
        contentMaxWidth={380}
        zIndex={sheetOpen ? 10002 : 1000}
        showBackdrop={false}
        headerRight={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSheetOpen(true)}
            style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10 }}
            title="Otworz karte wzorow (Ctrl+Alt+W)"
          >
            Karta wzorow
          </button>
        }
      >
        {calcOpen ? <ExamCalculator embedded /> : null}
      </EdgeDrawer>

      <FormulaSheet
        isOpen={sheetOpen}
        onOpenChange={setSheetOpen}
        width={FORMULA_SHEET_DRAWER_WIDTH}
        pdfPath={WZORY_URL}
        showBackdrop={false}
        headerRightExtra={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setCalcOpen(true)}
            style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10 }}
            title="Otworz kalkulator (Ctrl+Alt+K)"
          >
            Kalkulator
          </button>
        }
      />
    </div>
  );
}
